import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { memo, useCallback, useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Tooltip } from 'core-components/library/tooltip'
import {
  changeItemOrder,
  updateAssortmentItem,
} from 'partner360/features/assortment/assortmentSlice'

import { IAssortmentItem } from '../../../../features/assortment/models/data/IAssortmentItem'
import { DRAG_ASSORTIMENT_ITEM_KEY } from '../../configs'
import {
  IAssortmentDropMonitor,
  IDraggableAssortmentItem,
  IFindItemById,
} from '../../models/assortmentDnd'
import { TRANSLATION_KEYS } from '../../translations'

import { Comments } from './components/comments/Comments'
import { Configurations } from './components/configurations/Configurations'
import { DealStatus } from './components/dealStatus/DealStatus'
import { FieldsSet } from './components/fieldsSet/FieldsSet'
import { FocusField } from './components/focusField/FocusField'
import { ProductCodes } from './components/productCodes/ProductCodes'

import styles from './AssortmentItem.module.scss'

interface IAssortmentItemProps {
  disableDragging?: boolean
  isEditing: boolean
  index: number
  card: IAssortmentItem
  findItemById: IFindItemById<IAssortmentItem>
  onDeleteCard: (card: IAssortmentItem) => void
  onResetOrder?: (itemId: number, groupId: number, index: number) => void
  onSaveOrder: () => void
}

export const AssortmentItem = memo<IAssortmentItemProps>(
  ({
    disableDragging = false,
    isEditing,
    index,
    card,
    findItemById,
    onDeleteCard,
    onResetOrder,
    onSaveOrder,
  }) => {
    const itemRef = useRef<HTMLDivElement>(null)
    const dispatch = useAppDispatch()

    const handleFocusFieldChange = useCallback(
      (focus: string) => {
        dispatch(
          updateAssortmentItem({
            ...card,
            Focus: focus,
          })
        )
      },
      [dispatch, card]
    )

    const [, drag] = useDrag<IDraggableAssortmentItem>({
      type: DRAG_ASSORTIMENT_ITEM_KEY,
      item: {
        index,
        itemId: card.EntryId,
        groupId: card.AssortmentGroupId,
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      // canDrag: (monitor) => {
      //   if (disableDragging) {
      //     return false
      //   }
      //   return monitor.canDrag()
      // },
      end: (droppedItem, monitor) => {
        const { itemId, groupId, index } = droppedItem
        if (!monitor.didDrop() && onResetOrder) {
          onResetOrder(itemId, groupId, index)
        }
      },
    })

    const [{ handlerId }, drop] = useDrop<IDraggableAssortmentItem, void, IAssortmentDropMonitor>({
      accept: DRAG_ASSORTIMENT_ITEM_KEY,
      collect: (monitor) => ({
        handlerId: monitor.getHandlerId(),
      }),
      hover: (draggedItem) => {
        if (draggedItem.itemId !== card.EntryId) {
          const { index } = findItemById(card.EntryId)

          dispatch(
            changeItemOrder({
              itemId: draggedItem.itemId,
              targetIndex: index,
            })
          )
          // onChangeOrder(draggedItem.itemId, index)
        }
      },
      drop: () => {
        onSaveOrder()
      },
    })

    drag(drop(itemRef))

    return (
      <div className={styles.card} ref={itemRef} data-handler-id={handlerId}>
        <div className={styles.actionControls}>
          <RenderWhen condition={isEditing}>
            <Tooltip
              tipContent={() => <Translation text={TRANSLATION_KEYS.item.tooltips.deleteTooltip} />}
            >
              <Button
                color="grey"
                className={styles.actionButton}
                variant="text"
                iconButton="kbase kbase-delete"
                onClick={() => onDeleteCard(card)}
              />
            </Tooltip>
          </RenderWhen>
        </div>

        <div className={styles.cardHeader}>
          <FocusField
            isEditMode={isEditing}
            focus={card.Focus}
            focusLabel={TRANSLATION_KEYS.item.focus}
            onFocusChange={handleFocusFieldChange}
            suppliers={card.Suppliers}
            suppliersLabel={TRANSLATION_KEYS.item.suppliersLabel}
          />
        </div>
        <div className={styles.cardContent}>
          <FieldsSet isEditMode={isEditing} item={card} />
          <Configurations
            isEditMode={isEditing}
            item={card}
            labelKey={TRANSLATION_KEYS.item.configurations}
          />
          <DealStatus isEditMode={isEditing} item={card} />
          <ProductCodes isEditMode={isEditing} item={card} />
          <Comments isEditMode={isEditing} item={card} labelKey={TRANSLATION_KEYS.item.comments} />
        </div>
      </div>
    )
  }
)
